import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'

import { QuoteEventInputSchema, type QuoteEventInput } from '@/gen/cortado/v1/cortado_pb'
import { dateToTimestamp } from '@/lib/date-utils'

/** A quote event while it's being edited, before it's saved. */
export interface QuoteEventFormValue {
  /** Temporary client-side ID for tracking (not persisted) */
  tempId: string
  /** Server-assigned ID (only present when editing existing events) */
  id?: string
  eventDate?: Date
  eventLength?: number
  eventGuests?: number
  eventTimezone?: string
  eventLocation?: string
  baristaCount?: number
  cartCount?: number
  /** The inquiry (lead) event this was copied from, saved as `source_inquiry_event_id`. */
  sourceInquiryEventId?: string
}

/**
 * The inquiry (lead) event fields a quote event is copied from: a lead's own
 * event (`id`) or a pre-filled quote event that names its source.
 */
export interface CopyableInquiryEvent {
  id?: string
  sourceInquiryEventId?: string
  eventDate?: Timestamp
  eventLength?: number
  eventGuests?: number
  eventTimezone?: string
  eventLocation?: string
  baristaCount?: number
  cartCount?: number
}

/**
 * Copies a lead's events into quote-event form values — what "Replace from
 * lead" puts on a quote. Each copy gets a fresh client-side `tempId`.
 */
export function inquiryEventsToQuoteEventFormValues(inquiryEvents: CopyableInquiryEvent[]): QuoteEventFormValue[] {
  return inquiryEvents.map((inquiryEvent) => {
    // Convert proto Timestamp to Date
    let eventDate: Date | undefined
    if (inquiryEvent.eventDate) {
      const seconds = typeof inquiryEvent.eventDate.seconds === 'bigint'
        ? Number(inquiryEvent.eventDate.seconds)
        : inquiryEvent.eventDate.seconds
      eventDate = new Date(seconds * 1000)
    }

    return {
      tempId: crypto.randomUUID(),
      eventDate,
      eventLength: inquiryEvent.eventLength,
      eventGuests: inquiryEvent.eventGuests,
      eventTimezone: inquiryEvent.eventTimezone,
      eventLocation: inquiryEvent.eventLocation,
      baristaCount: inquiryEvent.baristaCount,
      cartCount: inquiryEvent.cartCount,
      sourceInquiryEventId: inquiryEvent.sourceInquiryEventId || inquiryEvent.id || undefined,
    }
  })
}

/** Quote-event form values as the `quote_events` inputs of a Create/UpdateQuote request. */
export function quoteEventFormValuesToInputs(events: QuoteEventFormValue[]): QuoteEventInput[] {
  return events.map((qe) =>
    create(QuoteEventInputSchema, {
      eventDate: qe.eventDate ? dateToTimestamp(qe.eventDate) : undefined,
      eventLength: qe.eventLength,
      eventGuests: qe.eventGuests,
      eventTimezone: qe.eventTimezone,
      eventLocation: qe.eventLocation,
      baristaCount: qe.baristaCount,
      cartCount: qe.cartCount,
      sourceInquiryEventId: qe.sourceInquiryEventId || undefined,
    })
  )
}
